'use client';
import { useEffect, useRef } from 'react';

import { generateSrcSet } from '@core/Components/HeycarImage/GenerateSrcSet';
import { HeycarImage } from '@core/Components/HeycarImage/HeycarImage';
import { IMAGE_SIZES, SRC_SET_WIDTHS } from '@core/Constants/ImageCarousel.constants';
import { debounce } from '@core/Utils/Debounce';

import {
  purchaseProLoader,
  purchaseProNativeRatioLoader,
} from '../PpImage/Utils/PurchaseProLoader';
import { CarouselFallbackImage } from './CarouselFallbackImage';
import { useZoom } from './Hooks/UseZoom';
import type { WholesaleImageCarouselProps } from './ImageCarousel.entity';
import ImageCarouselDots from './ImageCarouselDots/ImageCarouselDots';

import styles from './ImageCarousel.module.css';

export const WholesaleImageCarousel = ({
  images,
  imageOnClick,
  positionIndex,
  index,
  strings,
  visibleOverflowView,
  shouldShowDotCarousel = true,
  size = 'large',
  carouselProps,
  isStockUnavailable = false,
  isZoomEnabled = false,
  isInspectionReport = false,
}: WholesaleImageCarouselProps) => {
  const totalImgs = images.length;
  const hasMultipleImages = totalImgs > 1;
  const lastWindowWidth = useRef(typeof window === 'undefined' ? 0 : window.innerWidth);
  const { currentIndex, dragStart, dragging, dragEnd, moveTo, fromIndex, toIndex, carouselRef } =
    carouselProps;
  const { zoomHandleClick, ...touchEvents } = useZoom(isZoomEnabled ? carouselRef : null);
  // the full-screen slideshow shows images uncropped; other sizes use the 1.52:1 smart crop
  const isNativeRatio = size === 'pdpPurchaseProSlideshow';
  const loader = isNativeRatio ? purchaseProNativeRatioLoader : purchaseProLoader;

  function handleResize() {
    const windowWidth = typeof window === 'undefined' ? 0 : window.innerWidth;
    const threshold = 1;
    if (Math.abs(windowWidth - lastWindowWidth.current) > threshold) {
      lastWindowWidth.current = windowWidth;
      debounce(() => moveTo(currentIndex), 500)();
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [moveTo, currentIndex]);

  useEffect(() => {
    if (typeof positionIndex === 'number') moveTo(positionIndex);
    //Keeping carouselRef.current in dependencies helps issues with trying to set
  }, [positionIndex, carouselRef.current]);

  return (
    <section
      className={`${styles.wrapper} ${visibleOverflowView ? styles.visibleOverflow : ''}`}
      role="group"
      aria-roledescription="carousel"
      aria-label={strings.carouselLabel}
      data-test-id="image-carousel"
      data-size={size}
      data-inspection-report={isInspectionReport}
      onTouchStart={e => dragStart(e)}
      onTouchMove={e => dragging(e)}
      onTouchEnd={dragEnd}
    >
      <div
        className={`${styles.images} ${isZoomEnabled ? styles.hasZoom : ''}`}
        data-reservedstatus={isStockUnavailable}
        data-test-id="image-carousel-images"
        ref={carouselRef}
      >
        {images.map((image, i) => {
          const { isFallback } = image;
          if (isFallback) return null;
          const imageWidths = SRC_SET_WIDTHS[size];
          const preloadWidth = imageWidths?.[0] ?? 565;
          if (i >= Math.max(0, currentIndex - 1) && i <= currentIndex + 2)
            return (
              <link
                key={`${image.url}-${i + 1}`}
                rel="preload"
                as="image"
                fetchPriority="low"
                href={loader({ src: image.url, width: preloadWidth })}
                imageSrcSet={generateSrcSet(image.url, loader, imageWidths)}
                imageSizes={IMAGE_SIZES[size]}
              />
            );
        })}
        {images.map((image, i) => {
          const { url, title, description, isFallback, fallbackLabel } = image;
          const showCaption = title || description;
          if (i <= currentIndex + 2)
            return (
              <div
                key={`${url || 'fallback'}-${i + 1}`}
                className={`${styles.imageSlide} ${i === currentIndex ? 'current' : ''}`}
                role="group"
                aria-label={`${strings.slide} ${i + 1} ${strings.of} ${totalImgs}`}
                onClick={() => {
                  imageOnClick?.(i);
                  zoomHandleClick?.();
                }}
                data-test-id={`image-carousel-image-${i + 1}`}
              >
                {isFallback && fallbackLabel ? (
                  <CarouselFallbackImage
                    label={fallbackLabel}
                    variant={isInspectionReport ? 'gallery' : 'default'}
                  />
                ) : (
                  <HeycarImage
                    src={url}
                    alt={image.altText || ''}
                    className={styles.image}
                    sizes={IMAGE_SIZES[size]}
                    srcSetWidths={SRC_SET_WIDTHS[size]}
                    loader={loader}
                    forceVisible={(index ?? 100) <= 1 && i === 0}
                    preload={i <= 1}
                    isStandAlone={isNativeRatio}
                    {...touchEvents}
                  />
                )}
                {showCaption && (
                  <div className={styles.caption}>
                    {title && <p className={styles.title}>{title}</p>}
                    {description && <p className={styles.desc}>{description}</p>}
                  </div>
                )}
              </div>
            );
        })}
      </div>
      {hasMultipleImages && (
        <ImageCarouselDots
          strings={strings}
          moveTo={moveTo}
          totalImgs={totalImgs}
          currentIndex={currentIndex}
          fromIndex={fromIndex}
          toIndex={toIndex}
          size={size}
          shouldShowDotCarousel={shouldShowDotCarousel}
        />
      )}
    </section>
  );
};
